import { I18n } from "@/i18n/i18n";

// Show holiday effect in December for users with Dutch language or Brussels timezone
function showHolidayEffect(): boolean {
    // month is 0-indexed
    const isDecember = new Date().getMonth() === 11;
    const isNl = I18n.locale() === "nl";
    // eslint-disable-next-line new-cap
    const isBrusselsTime = Intl.DateTimeFormat().resolvedOptions().timeZone === "Europe/Brussels";

    return isDecember && (isNl || isBrusselsTime);
}

let confettiModule: Promise<typeof import("canvas-confetti") | undefined> | undefined;

/**
 * Starts loading canvas-confetti, which pages only need once something was handed in.
 * Resolves to undefined when the chunk fails to load, for example offline or after a deploy
 * removed it, and tries again on the next call.
 */
export function loadCelebration(): Promise<typeof import("canvas-confetti") | undefined> {
    confettiModule ??= import("canvas-confetti").catch(() => {
        confettiModule = undefined;
        return undefined;
    });
    return confettiModule;
}

/** Fires confetti, or snow in December. */
export async function celebrateSuccess(): Promise<void> {
    const module = await loadCelebration();
    if (!module) {
        return;
    }
    const confetti = module.default;

    if (!showHolidayEffect()) {
        void confetti({
            disableForReducedMotion: true,
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
        });
        return;
    }

    const duration = 10_000;
    const rand = (min: number, max: number): number => Math.random() * (max - min) + min;
    const end = performance.now() + duration;
    let skew = 1;

    function frame(now: number): void {
        const timeLeft = end - now;
        if (timeLeft <= 0) {
            return;
        }

        const ticks = Math.max(200, 500 * (timeLeft / duration));
        skew = Math.max(0.8, skew - 0.001);

        void confetti({
            disableForReducedMotion: true,
            particleCount: 1,
            startVelocity: 0,
            ticks,
            origin: { x: Math.random(), y: Math.random() * skew - 0.2 },
            colors: ["#fff"],
            shapes: ["circle"],
            gravity: rand(0.4, 0.6),
            scalar: rand(0.4, 1),
            drift: rand(-0.4, 0.4),
        });

        requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
}

export function getPositiveEmoji(): string {
    let emojis = [
        "check-bold",
        "thumb-up-outline",
        "emoticon-happy-outline",
        "emoticon-excited-outline",
        "emoticon-cool-outline",
        "shimmer",
        "party-popper",
        "arm-flex-outline",
        "emoticon-kiss-outline",
        "robot-outline",
        "cow",
        "unicorn-variant",
    ];
    if (showHolidayEffect()) {
        emojis = ["snowflake", "pine-tree", "gift", "cookie", "snowman"];
    }
    return "mdi-" + emojis[Math.floor(Math.pow(Math.random(), 3) * emojis.length)];
}
